import { useInfiniteQuery } from "@tanstack/react-query";
import {
  GetNotificationChannels,
  GetNotificationLogDetails,
  GetNotificationSettingChangeLog,
  GetNotificationSettings,
  GetNotificationSettingAnalyticsReport,
  GetNotificationSettingAnalyticsSummary,
  GetNotificationSettingsV2,
  DeleteNotificationSetting,
  DeleteNotificationSettingVariant,
  PostNotificationSetting,
  PostNotificationSettingTest,
  PostNotificationSettingVariant,
  PostVariantChannel,
  PostResetVariantChannelLocalized,
  PostVariantChannelLocalized,
  PutNotificationChannel,
  PutNotificationSettingV2,
  PutNotificationSettingBatch,
  PutNotificationSettingVariant,
  PutVariantChannelLocalized,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { useCustomQuery } from "../custom-query";
import { useCustomMutation } from "../custom-mutation";

/**
 * GET /notification/setting
 */
export const useGetNotificationSettings = ({
  enabled,
}: {
  enabled?: boolean;
} = {}) => {
  return useCustomQuery({
    queryKey: ["notificationSettings"],
    queryFn: () => get<GetNotificationSettings>("/notification/setting"),
    gcTime: 0,
    enabled: enabled ?? true,
  });
};

/**
 * GET /notification/channel
 */
export const useGetNotificationChannels = () => {
  return useCustomQuery({
    queryKey: ["notificationChannels"],
    queryFn: () => get<GetNotificationChannels>("/notification/channel"),
  });
};

/**
 * PUT /notification/channel/order
 *
 * Sets the client-wide channel fallback order. Pass `applyToAllSettings: true`
 * to also cascade the new order onto every notification setting's channels.
 */
export const useUpdateNotificationChannels = () => {
  return useCustomMutation({
    mutationFn: (body: PutNotificationChannel["body"]) =>
      put<PutNotificationChannel>(`/notification/channel/order`, body),
  });
};

/**
 * GET /notification/log/[notificationLogId]
 */
export const useGetNotificationLogDetails = (notificationLogId: number) => {
  return useCustomQuery({
    queryKey: ["notificationLogDetails", notificationLogId],
    queryFn: () =>
      get<GetNotificationLogDetails>(`/notification/log/${notificationLogId}`),
  });
};

// ─────────────────────────────────────────────────────────
// V2 (event-based, variant-aware) notification setting API
// ─────────────────────────────────────────────────────────

export type NotificationSettingsV2Query = readonly ["notificationSettingsV2"];
export const notificationSettingsV2QueryKey: NotificationSettingsV2Query = [
  "notificationSettingsV2",
];

/**
 * GET /notification-setting
 */
export const useGetNotificationSettingsV2 = () => {
  return useCustomQuery({
    queryKey: notificationSettingsV2QueryKey,
    queryFn: () => get<GetNotificationSettingsV2>("/notification-setting"),
  });
};

/**
 * POST /notification-setting
 */
export const useCreateNotificationSettingV2 = () => {
  return useCustomMutation({
    mutationFn: (body: PostNotificationSetting["body"]) =>
      post<PostNotificationSetting>("/notification-setting", body),
    displayNotification: true,
  });
};

/**
 * PUT /notification-setting/:id
 */
export const useUpdateNotificationSettingV2 = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: PutNotificationSettingV2["params"]["id"];
      body: PutNotificationSettingV2["body"];
    }) =>
      put<PutNotificationSettingV2>(
        `/notification-setting/${id}` as PutNotificationSettingV2["path"],
        body,
      ),
  });
};

/**
 * DELETE /notification-setting/:id
 *
 * Soft-deletes a whole setting. Only creatable events qualify — the server
 * returns a 409 for the fixed one-per-client events, which are turned off
 * with `isEnabled` instead.
 */
export const useDeleteNotificationSettingV2 = () => {
  return useCustomMutation({
    mutationFn: ({ id }: { id: DeleteNotificationSetting["params"]["id"] }) =>
      del<DeleteNotificationSetting>(
        `/notification-setting/${id}` as DeleteNotificationSetting["path"],
      ),
    displayNotification: true,
  });
};

/**
 * PUT /notification-setting/:id/batch
 *
 * Atomic save for the manage flow: setting fields (enabled, timing, win-back)
 * plus the selected variant's active-channel set + order in one transaction.
 */
export const useBatchUpdateSetting = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: PutNotificationSettingBatch["params"]["id"];
      body: PutNotificationSettingBatch["body"];
    }) =>
      put<PutNotificationSettingBatch>(
        `/notification-setting/${id}/batch` as PutNotificationSettingBatch["path"],
        body,
      ),
  });
};

/**
 * POST /notification-setting/:id/variant
 */
export const useCreateVariant = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: PostNotificationSettingVariant["params"]["id"];
      body: PostNotificationSettingVariant["body"];
    }) =>
      post<PostNotificationSettingVariant>(
        `/notification-setting/${id}/variant` as PostNotificationSettingVariant["path"],
        body,
      ),
    displayNotification: true,
  });
};

/**
 * PUT /notification-setting/:id/variant/:variantId
 */
export const useUpdateVariant = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
      body,
    }: {
      id: PutNotificationSettingVariant["params"]["id"];
      variantId: PutNotificationSettingVariant["params"]["variantId"];
      body: PutNotificationSettingVariant["body"];
    }) =>
      put<PutNotificationSettingVariant>(
        `/notification-setting/${id}/variant/${variantId}` as PutNotificationSettingVariant["path"],
        body,
      ),
  });
};

/**
 * DELETE /notification-setting/:id/variant/:variantId
 *
 * Soft-deletes a non-default variant (sets `isDeleted=true` server-side).
 * The default variant cannot be deleted — the server returns a 400.
 */
export const useDeleteVariant = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
    }: {
      id: DeleteNotificationSettingVariant["params"]["id"];
      variantId: DeleteNotificationSettingVariant["params"]["variantId"];
    }) =>
      del<DeleteNotificationSettingVariant>(
        `/notification-setting/${id}/variant/${variantId}` as DeleteNotificationSettingVariant["path"],
      ),
    displayNotification: true,
  });
};

/**
 * POST /notification-setting/:id/variant/:variantId/channel
 */
export const useCreateVariantChannel = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
      body,
    }: {
      id: PostVariantChannel["params"]["id"];
      variantId: PostVariantChannel["params"]["variantId"];
      body: PostVariantChannel["body"];
    }) =>
      post<PostVariantChannel>(
        `/notification-setting/${id}/variant/${variantId}/channel` as PostVariantChannel["path"],
        body,
      ),
  });
};

/**
 * POST /notification-setting/:id/variant/:variantId/test
 *
 * Sends a one-off test message for the variant on the ticked channels, to an
 * operator-supplied email / phone. Per-channel outcomes come back in the
 * response — a 200 does not mean every ticked channel was sent.
 */
export const useSendNotificationTest = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
      body,
    }: {
      id: PostNotificationSettingTest["params"]["id"];
      variantId: PostNotificationSettingTest["params"]["variantId"];
      body: PostNotificationSettingTest["body"];
    }) =>
      post<PostNotificationSettingTest>(
        `/notification-setting/${id}/variant/${variantId}/test` as PostNotificationSettingTest["path"],
        body,
      ),
    // Surfaces the server's own message on failure — the 429 quota refusal and
    // the 422 missing-recipient errors are the operator's cue to fix the input.
    displayNotification: true,
  });
};

/**
 * POST /notification-setting/:id/variant/:variantId/channel/:channelId/localized
 */
export const useCreateVariantChannelLocalized = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
      channelId,
      body,
    }: {
      id: PostVariantChannelLocalized["params"]["id"];
      variantId: PostVariantChannelLocalized["params"]["variantId"];
      channelId: PostVariantChannelLocalized["params"]["channelId"];
      body: PostVariantChannelLocalized["body"];
    }) =>
      post<PostVariantChannelLocalized>(
        `/notification-setting/${id}/variant/${variantId}/channel/${channelId}/localized` as PostVariantChannelLocalized["path"],
        body,
      ),
  });
};

/**
 * PUT /notification-setting/:id/variant/:variantId/channel/:channelId/localized/:localizedId
 */
export const useUpdateVariantChannelLocalized = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
      channelId,
      localizedId,
      body,
    }: {
      id: PutVariantChannelLocalized["params"]["id"];
      variantId: PutVariantChannelLocalized["params"]["variantId"];
      channelId: PutVariantChannelLocalized["params"]["channelId"];
      localizedId: PutVariantChannelLocalized["params"]["localizedId"];
      body: PutVariantChannelLocalized["body"];
    }) =>
      put<PutVariantChannelLocalized>(
        `/notification-setting/${id}/variant/${variantId}/channel/${channelId}/localized/${localizedId}` as PutVariantChannelLocalized["path"],
        body,
      ),
  });
};

/**
 * POST /notification-setting/:id/variant/:variantId/channel/:channelId/reset-default
 *
 * Overwrites the localized row for (channel, language) with the system
 * default template and returns the updated row.
 */
export const useResetVariantChannelDefault = () => {
  return useCustomMutation({
    mutationFn: ({
      id,
      variantId,
      channelId,
      body,
    }: {
      id: PostResetVariantChannelLocalized["params"]["id"];
      variantId: PostResetVariantChannelLocalized["params"]["variantId"];
      channelId: PostResetVariantChannelLocalized["params"]["channelId"];
      body: PostResetVariantChannelLocalized["body"];
    }) =>
      post<PostResetVariantChannelLocalized>(
        `/notification-setting/${id}/variant/${variantId}/channel/${channelId}/reset-default` as PostResetVariantChannelLocalized["path"],
        body,
      ),
  });
};

/**
 * GET /notification-setting/:id/analytics/summary
 */
export const useGetNotificationSettingAnalyticsSummary = (
  settingId: number,
  options: { range?: "7d" | "14d" | "30d"; enabled?: boolean } = {},
) => {
  const { range = "14d", enabled = true } = options;
  return useCustomQuery({
    queryKey: [
      "notificationSettingAnalyticsSummary",
      settingId,
      range,
    ] as const,
    queryFn: () =>
      get<GetNotificationSettingAnalyticsSummary>(
        `/notification-setting/${settingId}/analytics/summary` as GetNotificationSettingAnalyticsSummary["path"],
        { range },
      ),
    enabled: enabled && settingId > 0,
  });
};

/**
 * GET /notification-setting/:id/analytics/report
 */
export const useGetNotificationSettingAnalyticsReport = (
  settingId: number,
  options: {
    period: GetNotificationSettingAnalyticsReport["query"];
    enabled?: boolean;
  },
) => {
  const { period, enabled = true } = options;
  return useCustomQuery({
    queryKey: [
      "notificationSettingAnalyticsReport",
      settingId,
      period,
    ] as const,
    queryFn: () =>
      get<GetNotificationSettingAnalyticsReport>(
        `/notification-setting/${settingId}/analytics/report` as GetNotificationSettingAnalyticsReport["path"],
        period,
      ),
    enabled: enabled && settingId > 0,
  });
};

const CHANGE_LOG_PAGE_SIZE = 30;

/**
 * GET /notification-setting/:id/change-log
 */
export const useGetNotificationSettingChangeLog = (
  settingId: number,
  options: { enabled?: boolean } = {},
) => {
  const { enabled = true } = options;
  return useInfiniteQuery({
    // Nested under the settings list key so every save that refreshes the list
    // also refreshes the history.
    queryKey: [
      ...notificationSettingsV2QueryKey,
      "changeLog",
      settingId,
    ] as const,
    queryFn: ({ pageParam }) =>
      get<GetNotificationSettingChangeLog>(
        `/notification-setting/${settingId}/change-log` as GetNotificationSettingChangeLog["path"],
        { limit: CHANGE_LOG_PAGE_SIZE, offset: pageParam },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const next = lastPage.offset + lastPage.entries.length;
      return next < lastPage.total ? next : undefined;
    },
    enabled: enabled && settingId > 0,
  });
};
